/**
 * "Questo e cio che ho capito" (WQ-B12): sezioni per dominio, ogni fatto con Conferma,
 * Correggi e Togli. I fatti sanitari restano marcati "da confermare" e tengono chiuso
 * il pulsante finale finche non sono tutti decisi (docx §6.1.8, API-CONTRACT §3).
 */
import { useState } from 'react'

import {
  ConfirmFactSheet,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  SecondaryButton,
  SummarySection,
  WhyText,
  WizardFrame,
} from '../components'
import type { SummaryData, SummaryItemData } from '../api/wizard'

export type SummaryScreenProps = {
  summary: SummaryData
  busy?: boolean
  onDecide: (factId: string, valueText: string, decisione: 'conferma' | 'togli') => void
  onComplete: () => void
}

export function SummaryScreen({ summary, busy = false, onDecide, onComplete }: SummaryScreenProps) {
  const [inConferma, setInConferma] = useState<SummaryItemData | null>(null)

  return (
    <WizardFrame band={<ProgressWords block="Questo è ciò che ho capito" progress="Riepilogo" />}>
      <QuestionTitle>Guardi con calma</QuestionTitle>
      <WhyText>
        {summary.can_complete
          ? 'Può confermare, correggere o togliere qualsiasi cosa, anche più avanti.'
          : 'Le cose di salute restano «da confermare» finché non mi dice lei che vanno bene.'}
      </WhyText>

      <div className="mt-4 flex flex-col gap-4">
        {summary.sections.map((sezione) => (
          <div key={sezione.domain}>
            <SummarySection
              title={sezione.title_it}
              items={sezione.items.map((voce) => ({
                label: voce.needs_confirm ? 'Da confermare' : 'Salvato',
                value: voce.value_text,
              }))}
            />
            <div className="mt-2 flex flex-col gap-2">
              {sezione.items.map((voce) => (
                <div key={voce.fact_id} className="flex flex-wrap gap-2.5" data-fact={voce.fact_id}>
                  <SecondaryButton grow disabled={busy} onClick={() => setInConferma(voce)}>
                    {voce.needs_confirm ? 'Confermi questa?' : 'Rivedi'}
                  </SecondaryButton>
                </div>
              ))}
            </div>
          </div>
        ))}

        {summary.preferences.length > 0 && (
          <SummarySection
            title="Le sue scelte"
            items={summary.preferences.map((preferenza) => ({
              label: preferenza.label_it,
              value: preferenza.value_it ?? undefined,
            }))}
          />
        )}
      </div>

      <div className="mt-5">
        <PrimaryButton disabled={busy || !summary.can_complete} onClick={onComplete}>
          Va tutto bene
        </PrimaryButton>
        {!summary.can_complete && (
          <p className="mt-2 text-why text-ink" data-testid="riepilogo-bloccato">
            Restano {summary.pending_health_fact_ids.length} cose di salute da confermare o togliere.
          </p>
        )}
      </div>

      {inConferma && (
        <ConfirmFactSheet
          statement={inConferma.value_text}
          onYes={() => {
            onDecide(inConferma.fact_id, inConferma.value_text, 'conferma')
            setInConferma(null)
          }}
          onNo={() => {
            onDecide(inConferma.fact_id, inConferma.value_text, 'togli')
            setInConferma(null)
          }}
          onCorrect={() => setInConferma(null)}
        />
      )}
    </WizardFrame>
  )
}
